<script setup lang="ts">
import type { QuestionnaireDynamicFormField } from '@/types/questionnaire';
import type DynamicQuestionnaireForm from './DynamicQuestionnaireForm.vue';

const emit = defineEmits<{
	(e: 'onClose'): void
	(e: 'onBack'): void
	(e: 'onSubmitted'): void
}>();

const props = defineProps({
	formId: {
		type: Number as PropType<number | null>,
		required: true,
	},
	isOpen: {
		type: Boolean,
		default: false,
	},
});

const questionnaireStore = useQuestionnaireStore();
const checkoutStore = useCheckoutStore();
const questionnaireFormRef = ref<InstanceType<typeof DynamicQuestionnaireForm>>();

const isLoading = computed(() => questionnaireStore.form.isLoading);
const isSubmitting = computed(() => checkoutStore.surveyFormResponse.isLoading || checkoutStore.checkoutOrderResponse.isLoading);

const fields = computed(() => questionnaireStore.questionnaireFormDetail?.fields.map((field): QuestionnaireDynamicFormField => {
	return {
		...field,
		fieldName: `${field.id}`, // Use field's id as prop name
	};
}));

function handleFormChange(field: QuestionnaireDynamicFormField, value: string) {
	checkoutStore.surveyFormData[field.fieldName] = value;
}

function handleGoToNextStep() {
	const isFormError = questionnaireFormRef.value?.isFormError();
	if (isFormError) {
		return;
	}
	emit('onSubmitted');
}

function handleBackToPreviousStep() {
	emit('onBack');
}

watch(() => props.isOpen, (newIsOpen) => {
	if (newIsOpen && props.formId) {
		questionnaireStore.getQuestionnaireById(props.formId);
	}
});

watch(fields, (newFields) => {
	if (!newFields) {
		return;
	}
	const hasSurveyForm = !!Object.keys(checkoutStore.surveyFormData).length;
	if (!hasSurveyForm) {
		Object.assign(checkoutStore.surveyFormData, getDynamicFormFields(newFields));
	}
});

</script>
<template>
  <BaseSideDrawer
    :is-open="isOpen"
    @on-close="emit('onClose')"
  >
    <template #header>
      <div class="form-header">
        <DotPointsIcon />
        <h1 class="text-xl text-semibold">
          Questionnaire
        </h1>
      </div>
    </template>
    <template #content>
      <div
        v-if="isLoading"
        class="questionnaire-loaders"
      >
        <ButtonSkeletonLoader
          v-for="(_count, index) in 3"
          :key="index"
          height="44"
        />
      </div>
      <DynamicQuestionnaireForm
        v-else
        ref="questionnaireFormRef"
        :fields="fields"
        :form="checkoutStore.surveyFormData"
        @on-form-change="handleFormChange"
      />
    </template>
    <template #footer>
      <div
        v-if="isLoading"
        class="footer-loaders"
      >
        <ButtonSkeletonLoader
          v-for="(_count, index) in 2"
          :key="index"
          width="68"
          height="44"
        />
      </div>
      <div
        v-else
        class="form-footer"
      >
        <BaseButton
          size="md"
          color="gray"
          variant="outlined"
          :disabled="isSubmitting"
          @click="handleBackToPreviousStep"
        >
          Back
        </BaseButton>
        <BaseButton
          size="md"
          type="submit"
          :disabled="isSubmitting"
          @click="handleGoToNextStep"
        >
          Next
        </BaseButton>
      </div>
    </template>
  </BaseSideDrawer>
</template>
<style scoped lang="scss">
.form {
  &-header {
    display: flex;
    align-items: center;
    gap: spacings-get(2)
  }

  &-content {
    display: flex;
    flex-direction: column;
    gap: spacings-get(6);
  }

  &-footer {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: spacings-get(3);
    height: 100%;
    padding: 0 spacings-get(6);
    color: colors-get(base, white);
    border-top: 1px solid #{colors-get(gray, 200)};
  }
}

.questionnaire-loaders {
  display: flex;
  flex-direction: column;
  row-gap: spacings-get(6);
}

.footer-loaders {
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  column-gap: spacings-get(3);
  padding: 0 spacings-get(6);
  border-top: 1px solid #{colors-get(gray, 200)};
}
</style>